import React, { useEffect, useState } from 'react'
import { Form, Alert, Button } from 'react-bootstrap'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'

interface SPE {
  id: string
  business_name: string
}

interface DataScopeSelectorProps {
  companyId: string
  selectedResources: string[]
  onChange: (resources: string[]) => void
}

export const DataScopeSelector: React.FC<DataScopeSelectorProps> = ({
  companyId,
  selectedResources,
  onChange
}) => {
  const httpClient = useAxiosHttpClient()
  const [spes, setSpes] = useState<SPE[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSPEs()
  }, [companyId])

  const loadSPEs = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await httpClient.get?.({
        url: `${URL_API}/spes?company_id=${companyId}`
      })

      setSpes(response || [])
    } catch (err) {
      console.error('Error loading SPEs:', err)
      setError('Erro ao carregar SPEs')
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = (speId: string) => {
    const newResources = selectedResources.includes(speId)
      ? selectedResources.filter((id) => id !== speId)
      : [...selectedResources, speId]

    onChange(newResources)
  }

  const handleSelectAll = () => {
    onChange(spes.map((spe) => spe.id))
  }

  const handleClearAll = () => {
    onChange([])
  }

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="sr-only">Carregando SPEs...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>
  }

  if (spes.length === 0) {
    return (
      <Alert variant="warning">Nenhuma SPE encontrada para esta empresa.</Alert>
    )
  }

  return (
    <Form.Group>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="mb-0">
          Selecione as SPEs que o usuário pode acessar
        </Form.Label>
        <div>
          <Button
            variant="link"
            size="sm"
            onClick={handleSelectAll}
            className="p-0 mr-2"
          >
            Selecionar Todas
          </Button>
          <Button
            variant="link"
            size="sm"
            onClick={handleClearAll}
            className="p-0"
          >
            Limpar
          </Button>
        </div>
      </div>

      <div
        className="border rounded p-3"
        style={{ maxHeight: '300px', overflowY: 'auto' }}
      >
        {spes.map((spe) => (
          <Form.Check
            key={spe.id}
            type="checkbox"
            id={`spe-${spe.id}`}
            label={spe.business_name}
            checked={selectedResources.includes(spe.id)}
            onChange={() => handleToggle(spe.id)}
            className="mb-2"
          />
        ))}
      </div>

      {selectedResources.length > 0 && (
        <Form.Text className="text-muted">
          {selectedResources.length} SPE(s) selecionada(s)
        </Form.Text>
      )}

      {selectedResources.length === 0 && (
        <Form.Text className="text-danger">
          Por favor, selecione pelo menos uma SPE para acesso restrito
        </Form.Text>
      )}
    </Form.Group>
  )
}
